import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, IconButton } from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { v4 as uuid } from "uuid";

import { Input } from "Components/Form";
import { ItemJob } from "Models/sales";

import {
  ContentTitle,
  FormFieldLabel,
  StartingNumberStepperBox,
  StartingNumberStepperInput,
} from "./styles";
import { BingoSheetItems } from "./BingoSheetItems";

export const MAX_MANUAL_INVENTORY_ITEMS = 500;

export type BingoSheetFormType = {
  startingNumber?: number;
  quantity?: number;
  /** Single description applied to every generated item */
  defaultItemDescription?: string;
};

export function isManualInventoryFormValid(form: BingoSheetFormType | null): boolean {
  if (!form) return false;
  const sn = form.startingNumber;
  const q = form.quantity;
  if (
    typeof sn !== "number" ||
    !Number.isInteger(sn) ||
    sn < 1 ||
    typeof q !== "number" ||
    !Number.isInteger(q) ||
    q < 1 ||
    q > MAX_MANUAL_INVENTORY_ITEMS
  ) {
    return false;
  }
  return !!form.defaultItemDescription?.trim();
}

export function buildManualInventoryItemJobs(form: BingoSheetFormType): ItemJob[] {
  const quantity = form.quantity ?? 0;
  const startingNumber = form.startingNumber ?? 1;
  const description = form.defaultItemDescription?.trim() || "";

  const itemJobs: ItemJob[] = [];
  for (let index = 0; index < quantity; index += 1) {
    itemJobs.push({
      uuid: uuid(),
      description,
      quantity: 1,
      number: startingNumber + index,
      subStatus: "Uplifted",
      status: "Uplifted",
    });
  }
  return itemJobs;
}

interface IForm {
  bingoSheetForm: BingoSheetFormType | null;
  setBingoSheetForm: (value: BingoSheetFormType | null) => void;
}

const Form = ({ bingoSheetForm, setBingoSheetForm }: IForm) => {
  const { t: translate } = useTranslation();
  const [startingNumberDraft, setStartingNumberDraft] = useState("");

  const patchForm = (partial: Partial<BingoSheetFormType>) => {
    setBingoSheetForm({
      ...(bingoSheetForm || {}),
      ...partial,
    } as BingoSheetFormType);
  };

  const defaultDescriptionText = translate("defaultManualInventoryItemDescription");

  useEffect(() => {
    const sn = bingoSheetForm?.startingNumber;
    if (sn !== undefined) {
      setStartingNumberDraft(String(sn));
    } else {
      setStartingNumberDraft("");
    }
  }, [bingoSheetForm?.startingNumber]);

  const bumpStartingNumber = (delta: number) => {
    const current = bingoSheetForm?.startingNumber ?? 0;
    const next = current + delta;
    if (next < 1) {
      patchForm({ startingNumber: undefined });
      return;
    }
    patchForm({ startingNumber: next });
  };

  const onStartingNumberChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    setStartingNumberDraft(digits);
    if (digits === "") {
      patchForm({ startingNumber: undefined });
      return;
    }
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 1) {
      patchForm({ startingNumber: undefined });
      return;
    }
    patchForm({ startingNumber: n });
  };

  const onStartingNumberBlur = () => {
    const digits = startingNumberDraft.replace(/\D/g, "");
    if (digits === "") {
      patchForm({ startingNumber: 1 });
      setStartingNumberDraft("1");
      return;
    }
    let n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 1) {
      patchForm({ startingNumber: 1 });
      setStartingNumberDraft("1");
      return;
    }
    patchForm({ startingNumber: n });
    setStartingNumberDraft(String(n));
  };

  const onQuantityChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (digits === "") {
      patchForm({ quantity: undefined });
      return;
    }
    let n = parseInt(digits, 10);
    if (!Number.isFinite(n)) return;
    n = Math.min(MAX_MANUAL_INVENTORY_ITEMS, Math.max(1, n));
    const patch: Partial<BingoSheetFormType> = {
      quantity: n,
    };
    if (bingoSheetForm?.startingNumber === undefined) {
      patch.startingNumber = 1;
    }
    const prevDesc = bingoSheetForm?.defaultItemDescription;
    if (prevDesc === undefined || prevDesc === "") {
      patch.defaultItemDescription = defaultDescriptionText;
    }
    patchForm(patch);
  };

  const sn = bingoSheetForm?.startingNumber;

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ContentTitle>{translate("addManualItems")}</ContentTitle>
        </Grid>
        <Grid item container xs={12} spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <FormFieldLabel>
              {translate("startingNumber")} <span className="req">*</span>
            </FormFieldLabel>
            <StartingNumberStepperBox>
              <IconButton
                size="small"
                aria-label={translate("decreaseStartingNumber")}
                onClick={() => bumpStartingNumber(-1)}
                disabled={!sn || sn <= 1}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <StartingNumberStepperInput
                aria-label={translate("startingNumber")}
                inputMode="numeric"
                value={startingNumberDraft}
                onChange={(e) => onStartingNumberChange(e.target.value)}
                onBlur={onStartingNumberBlur}
              />
              <IconButton
                size="small"
                aria-label={translate("increaseStartingNumber")}
                onClick={() => bumpStartingNumber(1)}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </StartingNumberStepperBox>
          </Grid>
          <Grid item xs={12} md={4}>
            <Input
              label={
                <>
                  {translate("quantity")} <span style={{ color: "#D92D20" }}>*</span>
                </>
              }
              type="text"
              value={bingoSheetForm?.quantity ?? ""}
              onChange={(e) => onQuantityChange(e.target.value)}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 1,
                max: MAX_MANUAL_INVENTORY_ITEMS,
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Input
              label={
                <>
                  {translate("itemDescription")} <span style={{ color: "#D92D20" }}>*</span>
                </>
              }
              type="text"
              placeholder={translate("itemDescriptionDefaultPlaceholder")}
              value={bingoSheetForm?.defaultItemDescription ?? ""}
              onChange={(e) => patchForm({ defaultItemDescription: e.target.value })}
            />
          </Grid>
        </Grid>
      </Grid>

      <BingoSheetItems
        startingNumber={bingoSheetForm?.startingNumber || 1}
        quantity={bingoSheetForm?.quantity || 0}
      />
    </div>
  );
};

export const BingoSheetComponent = {
  Form,
  Bingo: BingoSheetItems,
};
